import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CircularProgress, Box, Alert } from '@mui/material';
import RoleBasedAccess from '../components/Auth/RoleBasedAccess';

// Import pages
import LoginPage from '../pages/auth/LoginPage';
import RegisterPage from '../pages/auth/RegisterPage';
import DocumentationPage from '../pages/DocumentationPage';
import DashboardPage from '../pages/dashboard/DashboardPage';
import ClientsPage from '../pages/clients/ClientsPage';
import ClientDetailPage from '../pages/clients/ClientDetailPage';
import StaffPage from '../pages/staff/StaffPage';
import RosterPage from '../pages/roster/RosterPage';
import NotesPage from '../pages/notes/NotesPage';
import ProfilePage from '../pages/profile/ProfilePage';
import ApiDebug from '../components/Debug/ApiDebug';
import StaffDetailPage from '../pages/staff/StaffDetailPage';
import BackendTestPage from '../pages/BackendTestPage';
import RoleTestPage from '../pages/RoleTestPage';
import TimeClockPage from '../pages/roster/TimeClockPage';
import UserShiftsPage from '../pages/roster/UserShiftsPage';
import RosterCalendarPage from '../pages/roster/RosterCalendarPage';
import ShiftManagementPage from '../pages/roster/ShiftManagementPage';
import PayrollPage from '../pages/payroll/PayrollPage';
import BillingMainPage from '../pages/billing/BillingMainPage';
import BillingPeriodsPage from '../pages/billing/BillingPeriodsPage';
import BillingPeriodDetailPage from '../pages/billing/BillingPeriodDetailPage';
import BillingRatesPage from '../pages/billing/BillingRatesPage';
import ClientInvoicesPage from '../pages/billing/ClientInvoicesPage';

// Protected Route wrapper
interface ProtectedRouteProps {
    children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    return <>{children}</>;
};

// Staff-only Route wrapper (only basic staff can access)
interface StaffOnlyRouteProps {
    children: React.ReactNode;
}

const StaffOnlyRoute: React.FC<StaffOnlyRouteProps> = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Get user's roles array (from .NET backend) or fall back to user_type (Django backend)
    const userRoles = user.roles || [user.user_type];

    // Helper function to get user's privilege level
    const getUserPrivilegeLevel = (): number => {
        // .NET backend roles
        if (userRoles.includes('Administrator')) return 4;
        if (userRoles.includes('Manager')) return 3;
        if (userRoles.includes('Supervisor')) return 2;
        if (userRoles.includes('Staff')) return 1;

        // Legacy Django backend roles (for backward compatibility)
        if (userRoles.includes('Admin')) return 4;
        if (userRoles.includes('Care_Coordinator')) return 2;
        if (userRoles.includes('Carer')) return 1;

        return 1; // Default to basic staff level
    };

    const privilegeLevel = getUserPrivilegeLevel();

    // Only allow basic staff (level 1)
    if (privilegeLevel === 1) {
        return <>{children}</>;
    }

    // Higher privilege users get access denied
    return (
        <Box sx={{ p: 3 }}>
            <Alert severity="error">
                Access Denied: This page is only available for staff members. Please use the Roster management interface instead.
            </Alert>
        </Box>
    );
};

// Role Protected Route wrapper
interface RoleProtectedRouteProps {
    children: React.ReactNode;
    requiredPrivilegeLevel: number;
    requiredRoles?: string[];
}

const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({ 
    children, 
    requiredPrivilegeLevel, 
    requiredRoles 
}) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    return (
        <RoleBasedAccess 
            requiredPrivilegeLevel={requiredPrivilegeLevel}
            requiredRoles={requiredRoles}
            fallback={
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">
                        Access Denied: You do not have permission to view this page.
                    </Alert>
                </Box>
            }
        >
            {children}
        </RoleBasedAccess>
    );
};

// Wrapper for ShiftManagementPage that connects to AuthContext
const ShiftManagementPageWrapper: React.FC = () => {
    const { user, getUserRole } = useAuth();
    
    if (!user) {
        return <Navigate to="/login" replace />;
    }

    const userRole = getUserRole();
    let roleType: 'staff' | 'administrator' | 'manager' | 'supervisor' = 'staff';
    
    // Map based on role names from backend
    switch (userRole.name.toLowerCase()) {
        case 'administrator':
            roleType = 'administrator';
            break;
        case 'manager':
            roleType = 'manager';
            break;
        case 'supervisor':
            roleType = 'supervisor';
            break;
        default:
            // Also check by level for fallback
            if (userRole.level >= 4) {
                roleType = 'administrator';
            } else if (userRole.level >= 3) {
                roleType = 'manager';
            } else if (userRole.level >= 2) {
                roleType = 'supervisor';
            } else {
                roleType = 'staff';
            }
            break;
    }

    console.log('Shift Management - User:', user.email, 'Role:', userRole.name, 'Level:', userRole.level, 'Mapped to:', roleType);

    return (
        <ShiftManagementPage 
            currentUserId={user.id}
            userRole={roleType}
        />
    );
};

// Smart redirect based on user role
const RoleBasedRedirect: React.FC = () => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Get user's roles array (from .NET backend) or fall back to user_type (Django backend)
    const userRoles = user.roles || [user.user_type];

    // Helper function to get user's privilege level
    const getUserPrivilegeLevel = (): number => {
        // .NET backend roles
        if (userRoles.includes('Administrator')) return 4;
        if (userRoles.includes('Manager')) return 3;
        if (userRoles.includes('Supervisor')) return 2;
        if (userRoles.includes('Staff')) return 1;

        // Legacy Django backend roles (for backward compatibility)
        if (userRoles.includes('Admin')) return 4;
        if (userRoles.includes('Care_Coordinator')) return 2;
        if (userRoles.includes('Carer')) return 1;

        return 1; // Default to basic staff level
    };

    const privilegeLevel = getUserPrivilegeLevel();

    // Redirect based on user role
    if (privilegeLevel >= 2) {
        // Care Coordinator and above go to dashboard
        return <Navigate to="/dashboard" replace />;
    } else {
        // Basic staff go directly to their shifts
        return <Navigate to="/user-shifts" replace />;
    }
};
// Public Route wrapper (redirect based on role if already logged in)
interface PublicRouteProps {
    children: React.ReactNode;
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (user) {
        // If user is already logged in, redirect them based on their role
        return <RoleBasedRedirect />;
    }

    return <>{children}</>;
};

const AppRoutes: React.FC = () => {
    const { user } = useAuth();

    return (
        <Box
            component="main"
            sx={{
                flexGrow: 1,
                p: 3,
                mt: user ? 8 : 0, // Account for AppBar height
                minHeight: '100vh',
            }}
        >
            <Routes>
                {/* Public routes */}
                <Route
                    path="/login"
                    element={
                        <PublicRoute>
                            <LoginPage />
                        </PublicRoute>
                    }
                />
                <Route
                    path="/register"
                    element={
                        <PublicRoute>
                            <RegisterPage />
                        </PublicRoute>
                    }
                />
                <Route
                    path="/documentation"
                    element={<DocumentationPage />}
                />

                {/* Protected routes */}
                <Route
                    path="/dashboard"
                    element={
                        <RoleProtectedRoute requiredPrivilegeLevel={2}>
                            <DashboardPage />
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/clients"
                    element={
                        <RoleProtectedRoute requiredPrivilegeLevel={2}>
                            <ClientsPage />
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/clients/:id"
                    element={
                        <RoleProtectedRoute requiredPrivilegeLevel={2}>
                            <ClientDetailPage />
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/staff"
                    element={
                        <RoleProtectedRoute requiredPrivilegeLevel={3}>
                            <StaffPage />
                        </RoleProtectedRoute>
                    }
                />
                <Route 
                    path="/staff/:id" 
                    element={
                        <RoleProtectedRoute requiredPrivilegeLevel={3}>
                            <StaffDetailPage />
                        </RoleProtectedRoute>
                    } 
                />
                <Route
                    path="/roster"
                    element={
                        <RoleProtectedRoute requiredPrivilegeLevel={2}>
                            <RosterPage />
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/roster/calendar"
                    element={
                        <RoleProtectedRoute requiredPrivilegeLevel={2}>
                            <RosterCalendarPage />
                        </RoleProtectedRoute>
                    }
                />
                {/* <Route
                    path="/time-clock"
                    element={
                        <ProtectedRoute>
                            <TimeClockPage />
                        </ProtectedRoute>
                    }
                /> */}
                <Route
                    path="/user-shifts"
                    element={
                        <StaffOnlyRoute>
                            <UserShiftsPage />
                        </StaffOnlyRoute>
                    }
                />
                <Route
                    path="/roster/shift-management"
                    element={
                        <RoleProtectedRoute requiredPrivilegeLevel={3}>
                            <ShiftManagementPageWrapper />
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/payroll"
                    element={
                        <RoleProtectedRoute requiredPrivilegeLevel={3}>
                            <PayrollPage />
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/billing"
                    element={
                        <RoleProtectedRoute requiredPrivilegeLevel={3}>
                            <BillingMainPage />
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/billing/periods"
                    element={
                        <RoleProtectedRoute requiredPrivilegeLevel={3}>
                            <BillingPeriodsPage />
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/billing/periods/:id"
                    element={
                        <RoleProtectedRoute requiredPrivilegeLevel={3}>
                            <BillingPeriodDetailPage />
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/billing/rates"
                    element={
                        <RoleProtectedRoute requiredPrivilegeLevel={3}>
                            <BillingRatesPage />
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/billing/client-invoices"
                    element={
                        <RoleProtectedRoute requiredPrivilegeLevel={3}>
                            <ClientInvoicesPage />
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/notes"
                    element={
                        <RoleProtectedRoute requiredPrivilegeLevel={2}>
                            <NotesPage />
                        </RoleProtectedRoute>
                    }
                />
                <Route
                    path="/profile"
                    element={
                        <ProtectedRoute>
                            <ProfilePage />
                        </ProtectedRoute>
                    }
                />

                {/* Debug route - only available in development */}
                <Route
                    path="/debug"
                    element={
                        <ProtectedRoute>
                            <ApiDebug />
                        </ProtectedRoute>
                    }
                />

                {/* Backend test route */}
                <Route
                    path="/test"
                    element={
                        <ProtectedRoute>
                            <BackendTestPage />
                        </ProtectedRoute>
                    }
                />

                {/* Role test route */}
                <Route
                    path="/role-test"
                    element={
                        <ProtectedRoute>
                            <RoleTestPage />
                        </ProtectedRoute>
                    }
                />

                {/* Default redirect */}
                <Route path="/" element={<RoleBasedRedirect />} />

                {/* Catch all route */}
                <Route
                    path="*"
                    element={
                        <ProtectedRoute>
                            <RoleBasedRedirect />
                        </ProtectedRoute>
                    }
                />
            </Routes>
        </Box>
    );
};

export default AppRoutes;
